/**
 * @fileoverview Exceções pela OS (aba "Conclusão" do editor de triagem): regras
 * como "Garantia é Em garantia E Categoria contém TV → Simone", com até 3 dados
 * ("E também"), todos precisando bater. Quando a IA conclui ou passa a
 * conversa, vale a primeira regra que bater, mesmo com "Encerrar a conversa".
 * Cada regra aparece fechada, numa linha com os dados e o destino; abre para
 * editar. Os valores são colados ou digitados, um por linha; maiúsculas,
 * acentos e espaços não importam (o servidor compara assim).
 */

import React, { useMemo } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";
import DestinationFields from "./DestinationFields";
import SectionTitle from "./InfoTip";
import {
  WORK_ORDER_ROUTE_FIELDS,
  WORK_ORDER_ROUTE_MATCHES,
  WORK_ORDER_ROUTE_MAX,
  WORK_ORDER_ROUTE_MAX_CONDITIONS,
  moveInList,
  newRouteCondition,
  newWorkOrderRoute,
  routeValues,
} from "./formState";

const r = (key, params) => i18n.t(`triage.manager.editor.routes.${key}`, params);

/** Um dado da regra: qual dado da OS, como comparar e os valores. */
const ConditionFields = ({ condition, usedFields, onChange }) => {
  const classes = useStyles();
  // O estado só compara a sigla inteira: "é um destes".
  const soIgual = condition.field === "state";
  return (
    <>
      <div className={classes.grid2}>
        <ModernSelect
          className={classes.field}
          size="small"
          fullWidth
          label={r("field")}
          InputLabelProps={{ shrink: true }}
          value={condition.field}
          onChange={(ev) => onChange({ field: ev.target.value, ...(ev.target.value === "state" && { match: "equals" }) })}
        >
          {WORK_ORDER_ROUTE_FIELDS.map((f) => (
            <MenuItem key={f} value={f} disabled={f !== condition.field && usedFields.includes(f)}>
              {r(`fields.${f}`)}
            </MenuItem>
          ))}
        </ModernSelect>
        <ModernSelect
          className={classes.field}
          size="small"
          fullWidth
          label={r("match")}
          InputLabelProps={{ shrink: true }}
          disabled={soIgual}
          value={soIgual ? "equals" : condition.match}
          onChange={(ev) => onChange({ match: ev.target.value })}
        >
          {WORK_ORDER_ROUTE_MATCHES.map((m) => (
            <MenuItem key={m} value={m}>
              {r(`matches.${m}`)}
            </MenuItem>
          ))}
        </ModernSelect>
      </div>
      <TextField
        className={classes.field}
        variant="outlined"
        size="small"
        fullWidth
        multiline
        minRows={3}
        maxRows={10}
        label={r("values")}
        placeholder={r(`placeholders.${condition.field}`)}
        helperText={r("valuesHint")}
        InputLabelProps={{ shrink: true }}
        value={condition.valuesText}
        onChange={(ev) => onChange({ valuesText: ev.target.value })}
      />
    </>
  );
};

/** "Categoria: Refrigerator +2": o dado e o primeiro valor (com quantos mais há). */
const resumoDoDado = (field, valores) => {
  const nome = r(`fields.${field}`);
  if (!valores.length) return `${nome}: ${r("noValue")}`;
  return `${nome}: ${valores[0]}${valores.length > 1 ? ` +${valores.length - 1}` : ""}`;
};

const WorkOrderRoutesEditor = ({ value, onChange, queues, users, removed = {}, onFixRemoved, workOrdersEnabled }) => {
  const classes = useStyles();
  const set = (uid, patch) => onChange(value.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));
  const setAlso = (rota, uid, patch) => set(rota.uid, { also: rota.also.map((c) => (c.uid === uid ? { ...c, ...patch } : c)) });
  // O resumo de cada regra (refeito só quando os dados ou os valores mudam).
  const textos = value.map((x) => [x, ...x.also].map((c) => `${c.field}\u0002${c.valuesText}`).join("\u0001")).join("\u0000");
  const resumos = useMemo(
    () => value.map((x) => [x, ...x.also].map((c) => resumoDoDado(c.field, routeValues(c.valuesText))).join(" · ")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textos]
  );
  // "Vai para Fila X · Simone".
  const destino = (rota) => {
    const nomes = [
      queues.find((q) => String(q.id) === String(rota.queueId))?.name,
      users.find((u) => String(u.id) === String(rota.userId))?.name,
    ].filter(Boolean);
    return nomes.length ? r("goesTo", { destination: nomes.join(" · ") }) : r("noDestination");
  };

  return (
    <div>
      <SectionTitle title={r("title")} info={r("info")} />
      {!workOrdersEnabled && value.length > 0 && <div className={classes.warnText}>{r("needsWorkOrders")}</div>}

      {value.map((rota, index) => {
        const usados = [rota.field, ...rota.also.map((c) => c.field)];
        // O dado a mais começa no primeiro que a regra ainda não usa (há 7, a regra usa até 3).
        const livre = WORK_ORDER_ROUTE_FIELDS.find((f) => !usados.includes(f));
        const alternar = () => set(rota.uid, { open: !rota.open });
        return (
          <div key={rota.uid} className={classes.item} style={{ marginTop: 12 }}>
            <div
              className={classes.itemHead}
              role="button"
              tabIndex={0}
              aria-expanded={!!rota.open}
              aria-label={r("editRule", { n: index + 1 })}
              onClick={alternar}
              onKeyDown={(ev) => {
                if (ev.target !== ev.currentTarget) return;
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  alternar();
                }
              }}
            >
              <span className={classes.itemOrder}>{index + 1}</span>
              <div className={classes.itemTitle}>
                <div className={`${classes.itemName} ${classes.itemNameWrap}`}>{resumos[index]}</div>
                <div className={classes.itemSub}>{destino(rota)}</div>
              </div>
              <div onClick={(ev) => ev.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={r("moveUp")}>
                  <span>
                    <button type="button" className={classes.iconBtn} disabled={index === 0} onClick={() => onChange(moveInList(value, index, -1))} aria-label={r("moveUp")}>
                      <ArrowUp />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title={r("moveDown")}>
                  <span>
                    <button
                      type="button"
                      className={classes.iconBtn}
                      disabled={index === value.length - 1}
                      onClick={() => onChange(moveInList(value, index, 1))}
                      aria-label={r("moveDown")}
                    >
                      <ArrowDown />
                    </button>
                  </span>
                </Tooltip>
                <Tooltip title={r("remove")}>
                  <span>
                    <button
                      type="button"
                      className={classes.iconBtn}
                      onClick={() => onChange(value.filter((x) => x.uid !== rota.uid))}
                      aria-label={r("remove")}
                    >
                      <Trash2 />
                    </button>
                  </span>
                </Tooltip>
              </div>
              <span className={classes.iconBtn} aria-hidden>
                {rota.open ? <ChevronDown /> : <ChevronRight />}
              </span>
            </div>
            {rota.open && (
            <div className={classes.itemBody}>
              <ConditionFields condition={rota} usedFields={usados} onChange={(patch) => set(rota.uid, patch)} />
              {rota.also.map((c) => (
                <React.Fragment key={c.uid}>
                  <div className={classes.andRow}>
                    <span>{r("and")}</span>
                    <Tooltip title={r("removeCondition")}>
                      <button
                        type="button"
                        className={classes.iconBtn}
                        onClick={() => set(rota.uid, { also: rota.also.filter((o) => o.uid !== c.uid) })}
                        aria-label={r("removeCondition")}
                      >
                        <Trash2 />
                      </button>
                    </Tooltip>
                  </div>
                  <ConditionFields condition={c} usedFields={usados} onChange={(patch) => setAlso(rota, c.uid, patch)} />
                </React.Fragment>
              ))}
              {usados.length < WORK_ORDER_ROUTE_MAX_CONDITIONS && (
                <div>
                  <button type="button" className={classes.btnGhost} onClick={() => set(rota.uid, { also: [...rota.also, newRouteCondition(livre)] })}>
                    <Plus size={14} style={{ marginRight: 6 }} />
                    {r("addCondition")}
                  </button>
                </div>
              )}
              <div className={classes.label}>{r("destination")}</div>
              <DestinationFields
                queueId={rota.queueId}
                userId={rota.userId}
                onChange={(d) => {
                  set(rota.uid, d);
                  if (removed[rota.uid] && onFixRemoved) onFixRemoved(rota.uid);
                }}
                queues={queues}
                users={users}
                removed={!!removed[rota.uid]}
              />
            </div>
            )}
          </div>
        );
      })}

      {value.length < WORK_ORDER_ROUTE_MAX && (
        <div style={{ marginTop: 12 }}>
          <button type="button" className={classes.btnGhost} onClick={() => onChange([...value, newWorkOrderRoute()])}>
            <Plus size={14} style={{ marginRight: 6 }} />
            {r("add")}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkOrderRoutesEditor;
